import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { HTMLAttributes, useCallback, useEffect } from 'react';
import { useTranslate } from '@tolgee/react';
import { regionLanguageMap } from 'services/config/region';
import { Descendant } from 'slate';
import toLowerCase from 'utils/toLowerCase';
import { RecommendTemplate, RecommendTemplateProps } from './RecommendTemplate';
import { Container } from './RecommendTemplates';
import useCrud from 'hooks/useCrud';
import { overseaTranslate } from 'services/api/overseaTranslate';
import { translateTemplate } from '../TranslateTool';
import {
  SlateValue2MustacheV2,
  mustache2SlateValueWithCatch,
} from 'components/TemplateEditor/mustacheUtils';

export interface RecommendTemplates2Props extends HTMLAttributes<HTMLDivElement> {
  region: General.UpperCaseRegion;
  langTemplatesMap?: Aide.LangTemplatesMap;
  onTemplateSelect: (template: Descendant[]) => void;
}

export function RecommendTemplates2(props: RecommendTemplates2Props) {
  const { region, langTemplatesMap = {}, onTemplateSelect, ...otherProps } = props;

  const { t } = useTranslate();

  const systemLang = useSystemLanguageValue();

  const regionLang = (
    {
      ...regionLanguageMap,
      my: 'ms',
      ph: 'ph',
    } as const
  )[toLowerCase(region)];

  const { [regionLang]: recommendTemplates } = langTemplatesMap;

  const { item: templates = [], fetch } = useCrud({
    read: useCallback(
      ({
        recommendTemplates,
        systemLang,
      }: {
        recommendTemplates: Aide.RecommendTemplate[];
        systemLang: General.SystemLanguage;
      }) => {
        return recommendTemplates.reduce<
          Promise<Pick<RecommendTemplateProps, 'regionData' | 'systemData'>[]>
        >((res, recommendTemplate) => {
          return res.then(async (res) => {
            const {
              lang,
              tag,
              tagTranslateMap,
              description,
              descriptionTranslateMap,
              template,
              templateTranslateMap,
            } = recommendTemplate;

            const regionData = {
              tag,
              description,
              replyTemplate: template,
            };

            if (lang === systemLang || (lang === 'ph' && systemLang === 'tl')) {
              res.push({
                regionData,
                systemData: regionData,
              });

              return res;
            }

            const targetLang = systemLang === 'tl' ? 'ph' : systemLang;

            const translatedTag =
              tagTranslateMap[targetLang] ||
              (await overseaTranslate(tag, systemLang, 'translateTool'));

            const translatedDescription = description
              ? descriptionTranslateMap[targetLang] ||
                (await overseaTranslate(description, systemLang, 'translateTool'))
              : description;

            const translatedTemplate: string =
              templateTranslateMap[targetLang] ||
              (await translateTemplate(
                mustache2SlateValueWithCatch(template.replace(/^#mustache#/, '')) || [],
                systemLang,
              ).then((value) => `#mustache#${SlateValue2MustacheV2(value)}`));

            const systemData = {
              tag: translatedTag,
              description: translatedDescription,
              replyTemplate: translatedTemplate,
            };

            res.push({
              regionData,
              systemData,
            });

            return res;
          });
        }, Promise.resolve([]));
      },
      [],
    ),
  });

  useEffect(() => {
    if (recommendTemplates) {
      fetch({
        recommendTemplates,
        systemLang,
      }).catch(() => {});
    }
  }, [fetch, recommendTemplates, systemLang]);

  if (templates.length === 0) {
    return null;
  }

  return (
    <Container {...otherProps}>
      <h4>{t('recommend_content')}</h4>
      <div>
        {templates.map((data) => {
          return <RecommendTemplate {...data} onTemplateSelect={onTemplateSelect} />;
        })}
      </div>
    </Container>
  );
}
